import { StatCard } from '@brush/ui';
import { Award, ChevronRight, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import type { RoomView } from '../nav';
import { useRoomSummary } from '../queries';
import { useRoom } from '../room';
import { contestsEmptyNotice } from '../summary';
import { totalsCaption, totalsCards } from '../overviewTotals';
import { OverviewTotalsSummary } from './OverviewTotalsSummary';
import { BoardsBehindSection } from './BoardsBehindSection';
import { RoomActivitySection } from './RoomActivitySection';

/**
 * How many rows either section shows before handing off to its own screen.
 *
 * This is a glance screen — the sections are summaries with a way through to the real list, so a
 * section that grows without bound has stopped being one.
 *
 * **Four, because the leaderboard list is a two-column grid from `md`.** Three filled a row and a
 * half and left a hole in the bottom right, which reads as a missing card rather than as the end
 * of a summary; four fills two clean rows with the more-row under them. Three was chosen first on
 * a phone measurement alone, and that was the wrong thing to optimise for — the fourth card costs
 * about 90px there and still leaves the **Fantasy contests** heading a short scroll away, while
 * the hole was on screen at every desktop width the product gets shown at.
 *
 * Capped at every size, not only on the phone. A desktop Overview listing fifteen boards is no
 * more readable, and two different truncations by width is a rule somebody has to maintain — the
 * app has no breakpoint hook and this is not the feature to introduce one for.
 */
const GLANCE_LIMIT = 4;

export function Overview({
  onNavigate,
  onChangePeriods,
}: {
  onNavigate: (view: RoomView) => void;
  /** Opens Settings → Preferences → Periods, where the totals period is chosen (UP24, A6). */
  onChangePeriods: () => void;
}) {
  const { room, roomId } = useRoom();
  // The same cached answer the shell's badges and rundown read: one small request, not the three whole
  // lists this screen used to fetch and reduce (performance rules 3 and 4).
  const summaryQ = useRoomSummary(roomId);

  if (!room) return null;

  const summary = summaryQ.data;
  // The totals cards under the totals period (UP24, A5): the period's figures with their comparison, or, under All time,
  // the figures as before 1a. The Players card stays all-time, and nothing below the cards follows the period (UP10).
  // The period is chosen in Settings → Preferences → Periods (A6); this screen states it and links there.
  const cards = totalsCards(summary, room.players);

  /*
    Decided by the server at the room's gaming day (ADR 0014), which is what keeps this screen and
    Leaderboards agreeing about "today" every evening west of Greenwich:

    - "Active" is the lifecycle status *and* the window, not the window alone — a board can sit inside
      its dates for weeks without anybody starting it, and only a started one scores through a points
      system. Soonest to end first: among boards really running, the one closing this week is the one
      an operator has something left to do about.
    - The drafts inside their range are counted, not listed: the empty state names them, so the honest
      filter does not read as a fault and the operator goes looking for Start rather than a missing board.
    - Contests in flight are Open and Locked, by name — the contest list's own order, since nothing on a
      contest says when it runs. Classified from the server's statuses, so nothing here compares a status
      string: testing for "completed", which the server never sends, listed closed contests as current
      (known gap 32).
  */
  const activeLeaderboards = summary?.leaderboards.running ?? [];
  const notStarted = summary?.leaderboards.notStartedInRange ?? 0;
  const openContests = summary?.contests.inFlight ?? [];

  return (
    <div className="p-8">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink-950">Overview</h1>
          <p className="mt-1 text-sm text-ink-800/60">{room.name} · {room.location}</p>
        </div>
        <OverviewTotalsSummary
          caption={totalsCaption(summary)}
          updating={summaryQ.isPlaceholderData}
          onChange={onChangePeriods}
        />
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <StatCard key={c.label} label={c.label} value={c.value} hint={c.hint} />
        ))}
      </div>

      {room.canLeaderboard ? (
        <section className="mb-6 overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
          <SectionHeader
            icon={<Award className="size-4 text-gold-500" />}
            title="Active leaderboards"
            total={activeLeaderboards.length}
            onViewAll={() => onNavigate('leaderboards')}
          />
          <div className="p-6">
            {activeLeaderboards.length === 0 ? (
              <p className="py-4 text-center text-sm text-ink-800/50">
                No leaderboards are running right now.
                {notStarted > 0 ? (
                  <>
                    {' '}
                    {notStarted === 1
                      ? 'One is inside its date range but has not been started.'
                      : `${notStarted} are inside their date range but have not been started.`}
                  </>
                ) : null}
              </p>
            ) : (
              <>
                <ul className="grid gap-3 md:grid-cols-2">
                  {activeLeaderboards.slice(0, GLANCE_LIMIT).map((lb) => (
                    <li key={lb.id} className="rounded-lg border border-ink-800/10 p-4">
                      <div className="font-medium text-ink-950">{lb.name}</div>
                      <div className="mt-0.5 text-xs text-ink-800/60">{lb.dateStart} — {lb.dateEnd}</div>
                      <div className="mt-2 text-xs text-ink-800/60">{lb.poolSize} tournaments</div>
                    </li>
                  ))}
                </ul>
                <MoreRow
                  hidden={activeLeaderboards.length - GLANCE_LIMIT}
                  noun="leaderboard"
                  onClick={() => onNavigate('leaderboards')}
                />
              </>
            )}
          </div>
        </section>
      ) : null}

      {room.canFantasy ? (
        <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
          <SectionHeader
            icon={<Sparkles className="size-4 text-gold-500" />}
            title="Fantasy contests"
            total={openContests.length}
            onViewAll={() => onNavigate('fantasy')}
          />
          <div className="p-6">
            {openContests.length === 0 ? (
              <p className="py-4 text-center text-sm text-ink-800/50">
                {contestsEmptyNotice(summary?.contests ?? { draft: 0, closed: 0 }, summary?.counts.fantasyContests ?? 0)}
              </p>
            ) : (
              <>
                <ul className="space-y-2">
                  {openContests.slice(0, GLANCE_LIMIT).map((f) => (
                    <li key={f.id} className="flex items-center justify-between rounded-lg border border-ink-800/10 p-3">
                      <div>
                        <div className="font-medium text-ink-950">{f.name}</div>
                        <div className="text-xs text-ink-800/60">{f.entries} entries · salary cap</div>
                      </div>
                      <span className="rounded-md bg-gold-500/15 px-2 py-1 text-xs font-medium text-ink-900">{f.status}</span>
                    </li>
                  ))}
                </ul>
                <MoreRow
                  hidden={openContests.length - GLANCE_LIMIT}
                  noun="contest"
                  onClick={() => onNavigate('fantasy')}
                />
              </>
            )}
          </div>
        </section>
      ) : null}

      <BoardsBehindSection room={room} onNavigate={onNavigate} />
      <RoomActivitySection room={room} />
    </div>
  );
}

/**
 * A section's heading, with **how many there are** beside it.
 *
 * The count is the honest half of capping the list: without it a section showing three of eleven
 * looks like a section with three in it, and the truncation becomes a quiet lie rather than a
 * summary. Same treatment the rail's nav gives its own counts.
 */
function SectionHeader({
  icon,
  title,
  total,
  onViewAll,
}: {
  icon: ReactNode;
  title: string;
  total: number;
  onViewAll: () => void;
}) {
  return (
    <header className="flex items-center justify-between gap-3 border-b border-ink-800/10 px-6 py-4">
      <h2 className="flex min-w-0 items-center gap-2 font-display font-semibold text-ink-950">
        {icon}
        <span className="truncate">{title}</span>
        {total > 0 ? <span className="num text-sm font-bold text-ink-800/40">{total}</span> : null}
      </h2>
      <button
        type="button"
        onClick={onViewAll}
        className="flex shrink-0 items-center gap-1 text-xs text-ink-800/60 hover:text-ink-950"
      >
        View all <ChevronRight className="size-3" />
      </button>
    </header>
  );
}

/** The way through to the rest, shown only when there is a rest. */
function MoreRow({
  hidden,
  noun,
  onClick,
}: {
  hidden: number;
  noun: string;
  onClick: () => void;
}) {
  if (hidden <= 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-1 rounded-lg border border-dashed border-ink-800/15 text-xs font-medium text-ink-800/60 transition hover:border-ink-800/30 hover:text-ink-950"
    >
      {hidden} more {noun}
      {hidden === 1 ? '' : 's'}
      <ChevronRight className="size-3" />
    </button>
  );
}
